import { useState, useEffect, useCallback, useMemo } from 'react';
import { useLocalStorage } from './useLocalStorage';
import {
    addSave,
    removeSave,
    toggleArchiveSave,
    markSaveAsRead,
    updateSave,
    moveSaveToTab,
    // serverTimestamp needs to be imported from firebase/firestore
} from '../utils/saveUtils';
import { fetchUrlMetadata } from '../utils/bookmarkUtils';
import { db } from '../firebase';
import { collection, onSnapshot, query, where, serverTimestamp } from 'firebase/firestore';
import { SAVE_SORT_OPTIONS, SAVE_READ_STATUS_FILTERS } from '../constants/saveConstants';
import { filterAndSortSaves, computeTabCounts } from '../utils/saveSelectors';
import { useCustomTabs } from './useCustomTabs';
import { useBookmarks } from './useBookmarks';

export const useSaves = (userId) => {
    const { addBookmark } = useBookmarks(userId);
    const [saves, setSaves] = useState([]);
    const [allSaves, setAllSaves] = useState([]);
    const [archivedSaves, setArchivedSaves] = useState([]);
    const [loading, setLoading] = useState(true);
    const [contentLoading, setContentLoading] = useState({});
    const [error, setError] = useState(null);
    const [viewMode, setViewMode] = useState('unread'); // 'active', 'read', 'unread', 'archived'
    const { tabs } = useCustomTabs(userId);
    const [sortBy, setSortBy] = useState(SAVE_SORT_OPTIONS.RECENT);
    const [searchTerm, setSearchTerm] = useState('');
    const [dateRange, setDateRange] = useState(null); // { from, to } or null
    const [allUniqueTags, setAllUniqueTags] = useState([]);
    const [selectedTags, setSelectedTags] = useState([]);
    // Read-status filter applied on top of the current view: 'all' | 'read' | 'unread'.
    const [readStatus, setReadStatus] = useState(SAVE_READ_STATUS_FILTERS.ALL);

    // Function to generate unique tags
    const generateUniqueTags = useCallback((activeSavesList, archivedSavesList) => {
        const uniqueTagsMap = new Map();
        const allSaves = [...activeSavesList, ...archivedSavesList];

        allSaves.forEach(save => {
            if (save.tags && Array.isArray(save.tags)) {
                save.tags.forEach(tag => {
                    const tagNameLower = tag.name.toLowerCase();
                    // Prefer updatedAt, then createdAt, then 0 as a fallback for timestamp
                    let lastUsedTimestamp = 0;
                    if (save.updatedAt && save.updatedAt.toDate) {
                        lastUsedTimestamp = save.updatedAt.toDate().getTime();
                    } else if (save.createdAt && save.createdAt.toDate) {
                        lastUsedTimestamp = save.createdAt.toDate().getTime();
                    }

                    const existingTag = uniqueTagsMap.get(tagNameLower);
                    if (!existingTag || lastUsedTimestamp > existingTag.lastUsed) {
                        uniqueTagsMap.set(tagNameLower, {
                            name: tag.name, // Preserve original casing for display
                            color: tag.color,
                            lastUsed: lastUsedTimestamp
                        });
                    }
                });
            }
        });
        setAllUniqueTags(Array.from(uniqueTagsMap.values()).sort((a,b) => a.name.localeCompare(b.name)));
    }, []);

    // Initialize saves with real-time updates
    useEffect(() => {
        if (!userId) return;
        setLoading(true);
        
        // Create a query for active saves
        const activeSavesQuery = query(
            collection(db, `users/${userId}/saves`),
            where('archived', '==', false)
        );

        // Create a query for archived saves
        const archivedSavesQuery = query(
            collection(db, `users/${userId}/saves`),
            where('archived', '==', true)
        );

        // Subscribe to active saves
        const unsubscribeActive = onSnapshot(activeSavesQuery, (snapshot) => {
            const savesList = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
                archived: false,
                read: doc.data().read || false
            }));
            setSaves(savesList);
            if (loading) {
                setLoading(false);
            }
        }, (err) => {
            console.error('Error loading active saves:', err);
            setError(err.message);
            setLoading(false);
        });

        // Subscribe to archived saves
        const unsubscribeArchived = onSnapshot(archivedSavesQuery, (snapshot) => {
            const savesList = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
                archived: true,
                read: doc.data().read || false
            }));
            setArchivedSaves(savesList);
        }, (err) => {
            console.error('Error loading archived saves:', err);
            setError(err.message);
        });

        return () => {
            unsubscribeActive();
            unsubscribeArchived();
        };
    }, [userId]);

    // Effect to update unique tags when saves or archivedSaves change
    useEffect(() => {
        generateUniqueTags(saves, archivedSaves);
        setAllSaves([...saves, ...archivedSaves]);
    }, [saves, archivedSaves, generateUniqueTags]);

    // All active filters in one object so the list contents and the tab
    // counters always derive from the exact same filter set.
    const filters = useMemo(() => ({
        searchTerm,
        selectedTags,
        dateRange,
        readStatus,
    }), [searchTerm, selectedTags, dateRange, readStatus]);

    // Single source of truth for the saves list: scope to the current view
    // (tab membership is additive), apply the shared filters, then sort.
    const filteredSaves = useMemo(() => filterAndSortSaves({
        saves,
        archivedSaves,
        viewMode,
        tabs,
        filters,
        sortBy,
    }), [saves, archivedSaves, viewMode, tabs, filters, sortBy]);

    const autoAssignToTab = useCallback(async (save) => {
        if (!save.tags || save.tags.length === 0) return;

        for (const tab of tabs) {
            if (tab.autoAddTags && tab.autoAddTags.length > 0) {
                const autoAddTagNames = tab.autoAddTags.map(t => t.toLowerCase());
                const hasMatchingTag = save.tags.some(tag => autoAddTagNames.includes(tag.name.toLowerCase()));

                if (hasMatchingTag && save.tabId !== tab.id) {
                    await moveSaveToTab(userId, save.id, tab.id);
                    // Update local state immediately
                    setSaves(prevSaves => prevSaves.map(s =>
                        s.id === save.id ? { ...s, tabId: tab.id } : s
                    ));
                    // No need to continue checking other tabs
                    break;
                }
            }
        }
    }, [tabs, userId]);

    // Add new save
    const handleAddSave = useCallback(async (url, saveData = {}) => {
        try {
            // Check if the URL already exists in active or archived saves
            // Combine saves and archivedSaves for checking to ensure URL uniqueness across both
            const allCurrentSaves = [...saves, ...archivedSaves];
            const existingSave = allCurrentSaves.find(save => save.url === url);

            if (existingSave) {
                // If save exists, update its createdAt and updatedAt timestamps to bump it
                // Also, ensure it's not archived if it was found in archivedSaves
                // and becomes an active save again.
                const updates = {
                    createdAt: serverTimestamp(),
                    updatedAt: serverTimestamp(),
                    archived: false, // Ensure it's active
                    ...saveData // Apply any new data provided
                };
                await updateSave(userId, existingSave.id, updates);

                // If it was archived, move it from archivedSaves to saves
                if (existingSave.archived) {
                    setArchivedSaves(prev => prev.filter(s => s.id !== existingSave.id));
                    // Add to saves, applying updates. Firestore listener will handle the final state.
                    // For immediate UI, we can simulate the update.
                    const bumpedSave = { ...existingSave, ...updates, createdAt: new Date(), updatedAt: new Date() };
                    setSaves(prev => [bumpedSave, ...prev.filter(s => s.id !== existingSave.id)]);
                }
                // No need to return a newSave object here, as we're updating.
                // The onSnapshot listener should handle UI updates.
                // Return the bumped save with an operation flag
                return { ...existingSave, ...updates, id: existingSave.id, operation: 'bumped' };
            } else {
                // If save does not exist, add it as a new save
                let dataToSave = { ...saveData, url };

                if (!dataToSave.title) {
                    const fetchedMetadata = await fetchUrlMetadata(url);
                    dataToSave = {
                        title: fetchedMetadata.title,
                        previewImage: fetchedMetadata.previewImage,
                        ...dataToSave
                    };
                }

                const newSave = await addSave(userId, dataToSave); // addSave from utils already sets createdAt

                // Update local storage after adding
                // The onSnapshot listener should ideally handle this, but for immediate UI response:
                if (!newSave.archived) { // newSave from addSave utility has `archived: false` by default
                    setSaves(prev => [newSave, ...prev]);
                }
                autoAssignToTab(newSave);
                // No need to add to localArchivedSaves here as it's a new, active save.
                return { ...newSave, operation: 'added' };
            }
        } catch (err) {
            setError(err.message);
            console.error('Error in handleAddSave:', err);
            throw err;
        }
    }, [userId, saves, archivedSaves, autoAssignToTab]);

    // Remove save
    const handleRemoveSave = useCallback(async (saveId) => {
        try {
            await removeSave(userId, saveId);
            // Update local storage after removal
            setSaves(prev => prev.filter(save => save.id !== saveId));
            setArchivedSaves(prev => prev.filter(save => save.id !== saveId));
        } catch (err) {
            setError(err.message);
            console.error('Error removing save:', err);
            throw err;
        }
    }, [userId]);

    const handleMoveSaveToTab = useCallback(async (saveId, tabId) => {
        try {
            await moveSaveToTab(userId, saveId, tabId);
            // Optimistically update the save in the local state
            setSaves(prevSaves => prevSaves.map(save =>
                save.id === saveId ? { ...save, tabId: tabId } : save
            ));
        } catch (err) {
            setError(err.message);
            console.error('Error moving save to tab:', err);
            throw err;
        }
    }, [userId]);

    const handleMoveSaveToBookmarks = useCallback(async (save) => {
        if (!save || !save.url) {
            console.error("Invalid save object provided.");
            return;
        }

        try {
            // The addBookmark function from useBookmarks already handles fetching metadata
            // and creating the bookmark in Firestore.
            await addBookmark(save.url, save.title);

            // Optionally, you might want to provide some user feedback,
            // like a toast notification, which could be handled in the UI component
            // that calls this function.
            console.log(`Save "${save.title}" has been added to bookmarks.`);

        } catch (err) {
            setError(err.message);
            console.error('Error moving save to bookmarks:', err);
            // Re-throw the error if you want the calling component to be able to catch it
            // and potentially show a more specific error message to the user.
            throw err;
        }
    }, [userId, addBookmark]);

    // Archive/Unarchive save
    const handleToggleArchive = useCallback(async (saveId, shouldArchive) => {
        try {
            await toggleArchiveSave(userId, saveId, shouldArchive);
            // Update local storage after toggling archive status
            if (shouldArchive) {
                setSaves(prev => {
                    const saveToArchive = prev.find(save => save.id === saveId);
                    if (saveToArchive) {
                        setArchivedSaves(archivedPrev => [...archivedPrev, { ...saveToArchive, archived: true }]);
                        return prev.filter(save => save.id !== saveId);
                    }
                    return prev;
                });
            } else {
                setArchivedSaves(prev => {
                    const saveToUnarchive = prev.find(save => save.id === saveId);
                    if (saveToUnarchive) {
                        setSaves(activePrev => [...activePrev, { ...saveToUnarchive, archived: false }]);
                        return prev.filter(save => save.id !== saveId);
                    }
                    return prev;
                });
            }
        } catch (err) {
            setError(err.message);
            console.error('Error toggling save archive status:', err);
            throw err;
        }
    }, [userId]);

    // Mark save as read/unread
    const handleMarkAsRead = useCallback(async (saveId, readStatus = true) => {
        try {
            await markSaveAsRead(userId, saveId, readStatus);
            // Optimistically update both in-memory and local storage state
            const updateReadStatus = prevSaves => prevSaves.map(save =>
                save.id === saveId ? { ...save, read: readStatus } : save
            );
            setSaves(updateReadStatus);
            setArchivedSaves(updateReadStatus);
        } catch (err) {
            setError(err.message);
            console.error('Error marking save as read/unread:', err);
            throw err;
        }
    }, [userId]);

    // Update save title
    const handleUpdateSaveTitle = useCallback(async (saveId, newTitle) => {
        try {
            await updateSave(userId, saveId, { title: newTitle });
            // Update local storage after title change
            setSaves(prev => prev.map(save =>
                save.id === saveId ? { ...save, title: newTitle } : save
            ));
            setArchivedSaves(prev => prev.map(save =>
                save.id === saveId ? { ...save, title: newTitle } : save
            ));
        } catch (err) {
            setError(err.message);
            console.error('Error updating save title:', err);
            throw err;
        }
    }, [userId]);

    // Fetch content for a save
    const handleFetchSaveContent = useCallback(async (saveId, forceRefresh = false) => {
        setContentLoading(prev => ({ ...prev, [saveId]: true }));
        try {
            const save = saves.find(s => s.id === saveId) || archivedSaves.find(s => s.id === saveId);
            if (!save) {
                throw new Error('Save not found');
            }
            // Check if content exists and is not forced to refresh
            if (save.content && !forceRefresh) {
                setContentLoading(prev => ({ ...prev, [saveId]: false }));
                return save;
            }
            const metadata = await fetchUrlMetadata(save.url);
            const updates = {
                content: metadata.content || '',
                contentFetchedAt: new Date().toISOString()
            };
            await updateSave(userId, saveId, updates);
            // Update local state to reflect changes immediately
            if (save.archived) {
                setArchivedSaves(prev => prev.map(s => 
                    s.id === saveId ? { ...s, ...updates } : s
                ));
            } else {
                setSaves(prev => prev.map(s => 
                    s.id === saveId ? { ...s, ...updates } : s
                ));
            }
            return { ...save, ...updates };
        } catch (err) {
            setError(err.message);
            console.error('Error fetching save content:', err);
            throw err;
        } finally {
            setContentLoading(prev => ({ ...prev, [saveId]: false }));
        }
    }, [userId, saves, archivedSaves]);

    // Generic update save data
    const handleUpdateSaveData = useCallback(async (saveId, updates) => {
        try {
            await updateSave(userId, saveId, updates);
            // Update local storage after data change
            let updatedSave = null;
            setSaves(prev => prev.map(save => {
                if (save.id === saveId) {
                    updatedSave = { ...save, ...updates };
                    return updatedSave;
                }
                return save;
            }));
            setArchivedSaves(prev => prev.map(save =>
                save.id === saveId ? { ...save, ...updates } : save
            ));

            if (updates.tags && updatedSave) {
                autoAssignToTab(updatedSave);
            }
        } catch (err) {
            setError(err.message);
            console.error('Error updating save data:', err);
            throw err;
        }
    }, [userId, autoAssignToTab]);

    // Set view mode
    const setViewModeValue = useCallback((mode) => {
        setViewMode(mode);
    }, []);

    // Single source of truth for the tab counters: computed through the same
    // scoping + filtering pipeline as the list (see saveSelectors), so the
    // counters update in real time whenever saves are added, removed,
    // re-qualified, marked read/unread, or any filter changes — with no tab
    // switch or page reload required.
    const counts = useMemo(
        () => computeTabCounts({ saves, archivedSaves, tabs, filters }),
        [saves, archivedSaves, tabs, filters]
    );

    return {
        saves: filteredSaves,
        allSaves,
        loading,
        contentLoading,
        error,
        viewMode,
        counts,
        tabs,
        addSave: handleAddSave,
        removeSave: handleRemoveSave,
        toggleArchive: handleToggleArchive,
        markAsRead: handleMarkAsRead,
        moveSaveToTab: handleMoveSaveToTab,
        moveSaveToBookmarks: handleMoveSaveToBookmarks,
        updateSaveTitle: handleUpdateSaveTitle,
        updateSaveData: handleUpdateSaveData,
        fetchSaveContent: handleFetchSaveContent,
        setViewMode: setViewModeValue,
        setSortBy,
        setSearchTerm,
        dateRange,
        setDateRange,
        allUniqueTags, // Return allUniqueTags
        selectedTags,
        setSelectedTags,
        readStatus,
        setReadStatus
    };
};
